const { apiUrl: API_URL, appBaseUrl: APP_BASE_URL } = resolveApiUrls();
const ADMIN_AUTH_STORAGE_KEY = "camioneroAdminBasicAuth";

export function getApiUrl() {
  return API_URL;
}

export function getAppBaseUrl() {
  return APP_BASE_URL;
}

export async function getJson(path) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: buildHeaders(path),
  });
  const data = await parseJsonResponse(response, `${API_URL}${path}`);

  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }

  return data;
}

export async function postForm(path, payload, files = {}, options = {}) {
  const formData = new FormData();
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || 0;

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });

  Object.entries(files).forEach(([fieldName, fileList]) => {
    const normalizedFiles = Array.isArray(fileList) ? fileList : [fileList];
    normalizedFiles.filter(Boolean).forEach((file) => {
      formData.append(fieldName, file);
    });
  });

  const timeoutId = timeoutMs
    ? setTimeout(() => {
        controller.abort();
      }, timeoutMs)
    : null;

  let response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: buildHeaders(path),
      body: formData,
      signal: controller.signal,
    });
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (error.name === "AbortError") {
      throw new Error(options.timeoutMessage || `Request timeout after ${timeoutMs}ms.`);
    }

    throw error;
  }

  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  const data = await parseJsonResponse(response, `${API_URL}${path}`);

  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }

  return data;
}

export async function postJson(path, payload, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || 0;
  const timeoutId = timeoutMs
    ? setTimeout(() => {
        controller.abort();
      }, timeoutMs)
    : null;

  let response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: {
        ...buildHeaders(path),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
      signal: controller.signal,
    });
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (error.name === "AbortError") {
      throw new Error(options.timeoutMessage || `Request timeout after ${timeoutMs}ms.`);
    }

    throw error;
  }

  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  const data = await parseJsonResponse(response, `${API_URL}${path}`);

  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }

  return data;
}

export async function patchJson(path, payload, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: "PATCH",
      headers: {
        ...buildHeaders(path),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
      signal: controller.signal,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Request failed.");
    }

    return data;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error(options.timeoutMessage || "La solicitud demoro demasiado.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function deleteJson(path, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: "DELETE",
      headers: buildHeaders(path),
      signal: controller.signal,
    });

    const data = await parseJsonResponse(response, `${API_URL}${path}`);

    if (!response.ok) {
      throw new Error(data.error || "Request failed.");
    }

    return data;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error(options.timeoutMessage || "La solicitud demoro demasiado.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getAbsoluteJson(path) {
  const response = await fetch(`${APP_BASE_URL}${path}`);
  const data = await parseJsonResponse(response, `${APP_BASE_URL}${path}`);

  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }

  return data;
}

async function parseJsonResponse(response, requestUrl) {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text();

  if (!contentType.includes("application/json")) {
    const htmlLike = rawText.trim().startsWith("<");
    if (htmlLike) {
      throw new Error(`La API respondio HTML en vez de JSON. Revisa la configuracion de API y la ruta ${requestUrl}.`);
    }
    throw new Error(`La API respondio un formato inesperado para ${requestUrl}.`);
  }

  try {
    return JSON.parse(rawText);
  } catch (_error) {
    throw new Error(`No pudimos interpretar la respuesta JSON de ${requestUrl}.`);
  }
}

export function saveAdminBasicAuth(username, password) {
  if (typeof window === "undefined") {
    return;
  }

  const token = window.btoa(`${username}:${password}`);
  window.sessionStorage.setItem(ADMIN_AUTH_STORAGE_KEY, token);
}

export function clearAdminBasicAuth() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
}

export function hasAdminBasicAuth() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(window.sessionStorage.getItem(ADMIN_AUTH_STORAGE_KEY));
}

function buildHeaders(path) {
  const headers = {};

  if (shouldAttachAdminAuth(path)) {
    const token = typeof window !== "undefined" ? window.sessionStorage.getItem(ADMIN_AUTH_STORAGE_KEY) : "";
    if (token) {
      headers.Authorization = `Basic ${token}`;
    }
  }

  return headers;
}

function shouldAttachAdminAuth(path) {
  if (!path) {
    return false;
  }

  if (path.startsWith("/admin")) {
    return true;
  }

  if (!path.startsWith("/document-policies")) {
    return false;
  }

  return path !== "/document-policies/catalog";
}

function resolveApiUrls() {
  const explicitBaseUrl =
    process.env.REACT_APP_API_BASE_URL || process.env.API_BASE_URL || "";
  const explicitApiUrl =
    process.env.REACT_APP_API_URL ||
    (explicitBaseUrl ? `${stripTrailingSlash(explicitBaseUrl)}/api` : "");
  const apiUrl = explicitApiUrl || "/api";
  const appBaseUrl = apiUrl.endsWith("/api") ? apiUrl.slice(0, -4) : apiUrl;

  return { apiUrl, appBaseUrl };
}

function stripTrailingSlash(value) {
  return String(value || "").replace(/\/+$/, "");
}
